import React from 'react';

/**
 * KSSpinner — Clean, lightweight circular spinner loader.
 * Ideal for buttons, form submits, modals, tables, cards, and data panels.
 *
 * Props:
 * - size: 'xs' (14px) | 'sm' (16px) | 'md' (24px) | 'lg' (32px) | 'xl' (40px)  (default: 'sm')
 * - color: 'current' | 'white' | 'amber' | 'emerald' | 'blue' | 'zinc'        (default: 'current')
 * - label: string (optional loading text displayed alongside spinner)
 * - centered: boolean (if true, centers horizontally in a padded container)
 * - className: string (extra classes for the spinner ring)
 * - containerClassName: string (extra classes for the wrapper if centered or labeled)
 * - style: object (inline styles for spinner ring)
 */
const SIZES = {
  xs: 'w-3.5 h-3.5 border-[2px]',
  sm: 'w-4 h-4 border-2',
  md: 'w-6 h-6 border-2',
  lg: 'w-8 h-8 border-[3px]',
  xl: 'w-10 h-10 border-[3px]',
};

const COLORS = {
  current: 'border-current',
  white: 'border-white',
  amber: 'border-amber-400',
  yellow: 'border-yellow-400',
  emerald: 'border-emerald-500',
  green: 'border-green-500',
  blue: 'border-blue-500',
  zinc: 'border-zinc-400',
  gray: 'border-zinc-400',
};

export default function KSSpinner({
  size = 'sm',
  color = 'current',
  label = null,
  centered = false,
  className = '',
  containerClassName = '',
  style = {},
}) {
  const sizeClass = SIZES[size] || (typeof size === 'string' && size.startsWith('w-') ? size : SIZES.sm);
  const colorClass = COLORS[color] || 'border-current';

  const spinner = (
    <span
      className={`inline-block shrink-0 rounded-full border-t-transparent animate-spin ${sizeClass} ${colorClass} ${className}`}
      style={style}
      role="status"
      aria-label={label || 'Loading...'}
    />
  );

  if (centered) {
    return (
      <div className={`flex flex-col sm:flex-row items-center justify-center w-full py-8 gap-2.5 text-zinc-400 text-sm ${containerClassName}`}>
        {spinner}
        {label && <span className="font-medium tracking-wide">{label}</span>}
      </div>
    );
  }

  if (label) {
    return (
      <span className={`inline-flex items-center gap-2 ${containerClassName}`}>
        {spinner}
        <span className="text-sm font-medium">{label}</span>
      </span>
    );
  }

  return spinner;
}
